import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isNotEmpty } from '@newfront-insurance/core';
import { cn } from '@newfront-insurance/core-ui/v2';
import { useCallback, useEffect, useRef } from 'react';

import { useAttachments } from './attachments-provider';
import { useChatInputContext, useDragHandlersRef } from './chat-input-context';
import { SkillMenu, useSkillMenu } from './skill-menu';
import { useSkillCommands } from '../../../../hooks/use-skill-commands';
import {
  getPlainTextFromElement,
  saveCursorPosition,
  restoreCursorPosition,
} from '../../../../lib/contenteditable-utils';
import {
  findMatchingSkillCommand,
  hasValidSkillCommandBoundary,
  renderSkillCommandContent,
} from '../../../../lib/skill-command-utils';
import type { SkillCommand } from '../../../../lib/skill-constants';
import { useChatOverlayContext } from '../../contexts/chat-overlay-context';

interface ChatInputTextareaProps {
  placeholder?: string;
  className?: string;
}

/**
 * Textarea component for chat input.
 * Handles text input, auto-height adjustment, and keyboard shortcuts.
 * Uses context to access input state and submit handler.
 * When skills are enabled, uses contenteditable for rich text rendering.
 */
export function ChatInputTextarea({ placeholder = 'Ask a question...', className }: ChatInputTextareaProps) {
  const { input, setInput, editorRef, handleSubmit, status } = useChatInputContext();
  const { accountUuid, userUuid, additionalContext } = useChatOverlayContext();
  const { attachments } = useAttachments();
  const handlersRef = useDragHandlersRef();
  const { handlePaste } = handlersRef.current;
  const hasUploadingFiles = attachments.some((attachment) => attachment.isUploading);
  const successfulAttachmentUuids = attachments
    .filter((attachment) => attachment.fileUuid && !attachment.uploadError)
    .map((attachment) => attachment.fileUuid!);
  const contextAttachmentFileUuids = Array.isArray(additionalContext?.attachmentFileUuids)
    ? additionalContext.attachmentFileUuids.filter((uuid): uuid is string => typeof uuid === 'string')
    : [];
  const hasUnsyncedAttachmentContext =
    successfulAttachmentUuids.length !== contextAttachmentFileUuids.length ||
    successfulAttachmentUuids.some((uuid) => !contextAttachmentFileUuids.includes(uuid));

  // Feature flag for prompt command autocomplete
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);

  // Skill menu state
  const skillMenu = useSkillMenu();
  const { commands } = useSkillCommands({
    accountUuid: isNovaSkillsEnabled ? accountUuid : undefined,
    userUuid: isNovaSkillsEnabled ? userUuid : undefined,
  });

  // Track if we're updating the content programmatically
  const isUpdatingRef = useRef(false);

  // Track if a skill was just selected from the menu (to prevent Enter from submitting)
  const justSelectedCommandRef = useRef(false);

  const adjustHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = `${editorRef.current.scrollHeight + 2}px`;
    }
  }, [editorRef]);

  const resetHeight = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.style.height = 'auto';
      editorRef.current.style.height = '92px';
    }
  }, [editorRef]);

  // Update the contenteditable display
  const updateDisplay = useCallback(
    (text: string, command: SkillCommand | null) => {
      const editor = editorRef.current;
      if (!editor || !(editor instanceof HTMLDivElement)) return;

      isUpdatingRef.current = true;
      const cursorPos = saveCursorPosition(editor);
      editor.innerHTML = renderSkillCommandContent(text, command) || '<br>';
      restoreCursorPosition(editor, cursorPos);
      isUpdatingRef.current = false;
    },
    [editorRef],
  );

  // Track the currently matched command handle for display purposes
  const matchedCommandRef = useRef<string | null>(null);

  // Handle input changes for contenteditable
  const handleContentEditableInput = useCallback(() => {
    const editor = editorRef.current;
    if (!editor || !(editor instanceof HTMLDivElement) || isUpdatingRef.current) return;

    const newValue = getPlainTextFromElement(editor);
    setInput(newValue);
    adjustHeight();

    // Auto-detect typed commands and update display
    const matchedCommand = findMatchingSkillCommand(newValue, commands);
    const currentStillValid =
      matchedCommandRef.current && hasValidSkillCommandBoundary(newValue, matchedCommandRef.current);

    // Handle command menu detection - don't reopen if a command is already matched
    if (newValue.startsWith('/') && !matchedCommand && !currentStillValid) {
      if (!skillMenu.isOpen) {
        skillMenu.open(newValue);
      } else {
        skillMenu.updateQuery(newValue);
      }
    } else if (skillMenu.isOpen) {
      skillMenu.close();
    }

    if (matchedCommand) {
      matchedCommandRef.current = matchedCommand.handle;
      skillMenu.close();
      updateDisplay(newValue, matchedCommand);
    } else if (!currentStillValid && matchedCommandRef.current) {
      matchedCommandRef.current = null;
      updateDisplay(newValue, null);
    }
  }, [editorRef, setInput, adjustHeight, commands, skillMenu, updateDisplay]);

  // Handle command selection from menu
  const handleSkillSelect = useCallback(
    (command: SkillCommand) => {
      const newInput = `/${command.handle} `;
      setInput(newInput);
      matchedCommandRef.current = command.handle;
      skillMenu.close();
      justSelectedCommandRef.current = true;

      // Update contenteditable content
      const editor = editorRef.current;
      if (editor instanceof HTMLDivElement) {
        editor.innerHTML = renderSkillCommandContent(newInput, command);
        // Place cursor right after the command handle + space, before the instruction placeholder
        const range = document.createRange();
        const placeholderEl = editor.querySelector('[data-instruction-placeholder]');
        if (placeholderEl) {
          range.setStartBefore(placeholderEl);
          range.collapse(true);
        } else {
          range.selectNodeContents(editor);
          range.collapse(false);
        }
        const selection = window.getSelection();
        selection?.removeAllRanges();
        selection?.addRange(range);
      }

      editorRef.current?.focus();
    },
    [setInput, skillMenu, editorRef],
  );

  const submitForm = useCallback(() => {
    if (hasUploadingFiles || hasUnsyncedAttachmentContext) {
      return;
    }

    handleSubmit(undefined);
    resetHeight();
    // Clear contenteditable content
    const editor = editorRef.current;
    if (editor instanceof HTMLDivElement) {
      editor.innerHTML = '<br>';
    }
  }, [hasUploadingFiles, hasUnsyncedAttachmentContext, handleSubmit, resetHeight, editorRef]);

  // Sync input state to contenteditable when input changes externally
  useEffect(() => {
    const editor = editorRef.current;
    if (!editor || !(editor instanceof HTMLDivElement)) return;

    const currentText = getPlainTextFromElement(editor);
    if (currentText !== input) {
      const matchedCommand = findMatchingSkillCommand(input, commands);
      updateDisplay(input, matchedCommand);
    }
  }, [input, commands, editorRef, updateDisplay]);

  // Initial height adjustment
  useEffect(() => {
    if (editorRef.current) {
      adjustHeight();
    }
  }, [adjustHeight, editorRef]);

  // Handle paste for contenteditable - strip formatting
  const handleContentEditablePaste = useCallback(
    (event: React.ClipboardEvent<HTMLDivElement>) => {
      event.preventDefault();
      const text = event.clipboardData.getData('text/plain');
      document.execCommand('insertText', false, text);

      // Also call the external paste handler for file handling
      if (handlePaste) {
        handlePaste(event as unknown as React.ClipboardEvent<HTMLTextAreaElement | HTMLDivElement>);
      }
    },
    [handlePaste],
  );

  // Common styles for the editor
  const editorStyles = cn(
    'max-h-[calc(25dvh)] min-h-[24px] resize-none overflow-y-auto',
    'bg-background !text-base transition-colors',
    'min-h-[92px] border-none focus-within:border-none hover:focus-within:border-none',
    'rounded-[10px] px-3 py-2.5',
    'focus:ring-0 focus:outline-none',
    'break-words whitespace-pre-wrap',
    className,
    {
      'rounded-b-[0px] border-b-0': isNotEmpty(attachments),
    },
  );

  // Handle keydown for both modes
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement | HTMLTextAreaElement>) => {
      // When skill menu is open, handle navigation keys
      if (isNovaSkillsEnabled && skillMenu.isOpen) {
        if (event.key === 'Escape') {
          event.preventDefault();
          skillMenu.close();
          return;
        }
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Tab' || event.key === 'Enter') {
          // Prevent default to stop cursor movement and let SkillMenu handle these
          event.preventDefault();
          return;
        }
      }

      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
        event.preventDefault();
        // Don't submit if a skill was just selected from the menu via Enter
        if (justSelectedCommandRef.current) {
          justSelectedCommandRef.current = false;
          return;
        }
        if (status === 'ready' && !hasUploadingFiles && !hasUnsyncedAttachmentContext) {
          submitForm();
        }
      }
    },
    [isNovaSkillsEnabled, skillMenu.isOpen, status, hasUploadingFiles, hasUnsyncedAttachmentContext, submitForm],
  );

  // Use contenteditable when prompt commands are enabled
  if (isNovaSkillsEnabled) {
    return (
      <div className="relative flex w-full flex-col gap-4">
        <SkillMenu
          isOpen={skillMenu.isOpen}
          onClose={skillMenu.close}
          onSelect={handleSkillSelect}
          commands={commands}
          searchQuery={skillMenu.searchQuery}
          anchorRef={editorRef as React.RefObject<HTMLDivElement>}
        />
        <div
          ref={editorRef as React.RefObject<HTMLDivElement>}
          role="textbox"
          aria-label={placeholder}
          tabIndex={0}
          contentEditable
          data-testid="chat-input"
          data-placeholder={placeholder}
          className={cn(
            editorStyles,
            'empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)]',
          )}
          onInput={handleContentEditableInput}
          onPaste={handleContentEditablePaste}
          onKeyDown={handleKeyDown}
          suppressContentEditableWarning
        />
      </div>
    );
  }

  // Fallback to regular textarea when skills are disabled
  return (
    <div className="relative flex w-full flex-col gap-4">
      <textarea
        data-testid="chat-input"
        ref={editorRef as React.RefObject<HTMLTextAreaElement>}
        placeholder={placeholder}
        value={input}
        onChange={(e) => {
          setInput(e.target.value);
          adjustHeight();
        }}
        onPaste={handlePaste as React.ClipboardEventHandler<HTMLTextAreaElement>}
        className={editorStyles}
        rows={2}
        autoFocus
        onKeyDown={handleKeyDown}
      />
    </div>
  );
}
